import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronRight, MoreHorizontal, Pencil, User } from "lucide-react";
import { Avatar, Chip, DataTableColumn } from "../../../../shared/ui/DataTable";
import { formatPermissionLabel } from "../../../../shared/lib";
import { Member } from "./types";
import { KeySquare, RepeatCircle, TagUser, UserRemove } from "iconsax-reactjs";
import { BodyText, HeadingText } from "../../../../shared/ui/typography";

const ADDITIONAL_PERMISSION_PREVIEW_MAX_CHARS = "View dashboard".length;
const ROLE_PREVIEW_MAX_CHARS = 18;

const truncatePermissionPreview = (value: string) => {
  if (value.length <= ADDITIONAL_PERMISSION_PREVIEW_MAX_CHARS) return value;
  return `${value.slice(0, ADDITIONAL_PERMISSION_PREVIEW_MAX_CHARS)}...`;
};

const truncateRolePreview = (value: string) => {
  if (value.length <= ROLE_PREVIEW_MAX_CHARS) return value;
  return `${value.slice(0, ROLE_PREVIEW_MAX_CHARS)}...`;
};

type MemberColumnsParams = {
  onOpenMember: (member: Member) => void;
  onAssignAccountManager: (member: Member) => void;
  onManageMember: (member: Member) => void;
  onEditRoles: (member: Member) => void;
  onAddPermissions: (member: Member) => void;
  onResendInvite: (member: Member) => void;
  onRemoveMember: (member: Member) => void;
};

export const buildMemberColumns = ({
  onOpenMember,
  onAssignAccountManager,
  onManageMember,
  onEditRoles,
  onAddPermissions,
  onResendInvite,
  onRemoveMember,
}: MemberColumnsParams): Array<DataTableColumn<Member>> => [
  {
    id: "name",
    header: "Name",
    cell: (m) => {
      const isPendingDuplicateEmail =
        !!m.showStatusDot &&
        m.name.trim().toLowerCase() === m.email.trim().toLowerCase();

      return (
        <div className="flex items-center gap-4">
          <Avatar
            name={m.name}
            email={m.email}
            showStatusDot={m.businesses.length > 0}
            managedBusinessesCount={m.businesses.length}
          />
          <div className="min-w-0 flex items-center gap-4">
            <div className="">
              <HeadingText className="truncate text-[14px] font-[500] text-[#161827]">
                {m.name}
              </HeadingText>
              {!isPendingDuplicateEmail ? (
                <BodyText className="truncate text-[14px] font-[400] text-[#6F739B]">
                  {m.email}
                </BodyText>
              ) : null}
            </div>

            <div>
              {m.showStatusDot ? (
                <span className="inline-flex py- shrink-0 self-center items-center justify-center rounded-full bg-[#E1E3F4] px-2 tracking-[-0.42px] text-[14px] font-[350] text-[#383B56]">
                  Pending
                </span>
              ) : null}
            </div>
          </div>
        </div>
      );
    },
    cellClassName: "min-w-[220px]",
  },
  {
    id: "role",
    header: "Role",
    cell: (m) => {
      if (!m.roles.length) {
        return <span className="text-[#6B6F8A]">-</span>;
      }

      const preview = m.roles[0];

      return (
        <div className="flex flex-wrap items-center gap-2">
          <Chip className="!rounded-[4px]" title={preview}>
            {truncateRolePreview(preview)}
          </Chip>
          {m.roles.length > 1 ? <Chip>+{m.roles.length - 1}</Chip> : null}
        </div>
      );
    },
    cellClassName: "min-w-[180px]",
  },
  {
    id: "permissions",
    header: "Additional permissions",
    cell: (m) => {
      if (!m.permissions.length) {
        return <span className="text-[#6B6F8A]">-</span>;
      }

      const formattedPermissions = m.permissions.map((permission) =>
        formatPermissionLabel(permission),
      );
      const preview = formattedPermissions[0];

      return (
        <div className="flex flex-wrap items-center gap-2">
          <Chip title={preview}>{truncatePermissionPreview(preview)}</Chip>
          {formattedPermissions.length > 1 ? (
            <Chip>+{formattedPermissions.length - 1}</Chip>
          ) : null}
        </div>
      );
    },
    cellClassName: "min-w-[180px]",
  },
  {
    id: "dateAdded",
    header: "Date added",
    cell: (m) => (
      <span className="text-[13px] font-[400] text-[#383B56]">
        {m.dateAdded}
      </span>
    ),
    cellClassName: "whitespace-nowrap min-w-[90px]",
  },
  {
    id: "actions",
    header: "",
    headerClassName: "w-[50px]",
    cellClassName: "whitespace-nowrap min-w-[60px]",
    cell: (m) => (
      <div
        className="flex items-center justify-end gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="grid h-9 w-9 place-items-center rounded-full hover:bg-[#F3F4FF]"
              aria-label={`More actions for ${m.name}`}
            >
              <MoreHorizontal className="text-[#2C2F44]" size={30} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={10}
              onClick={(event) => event.stopPropagation()}
              className="min-w-[260px] rounded-[20px] border border-[#E1E6FF] bg-white p-2 shadow-[0_20px_45px_rgba(15,20,40,0.15)]"
            >
              {m.showStatusDot ? (
                <DropdownMenu.Item
                  onSelect={(event) => {
                    event.stopPropagation();
                    onResendInvite(m);
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-3 text-[16px] font-[500] text-[#161827] outline-none transition-colors focus:bg-[#F5F6FD]"
                >
                  <RepeatCircle size={18} className="text-[#6E7397]" />
                  Resend invite
                </DropdownMenu.Item>
              ) : (
                <>
                  <DropdownMenu.Item
                    onSelect={(event) => {
                      event.stopPropagation();
                      onAssignAccountManager(m);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#2C2F44] outline-none transition-colors focus:bg-[#F5F6FD]"
                  >
                    <TagUser size={18} className="text-[#6E7397]" />
                    Assign as account manager
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={(event) => {
                      event.stopPropagation();
                      onManageMember(m);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#2C2F44] outline-none transition-colors focus:bg-[#F5F6FD]"
                  >
                    <User size={18} className="text-[#6E7397]" />
                    Manage
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={(event) => {
                      event.stopPropagation();
                      onEditRoles(m);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#2C2F44] outline-none transition-colors focus:bg-[#F5F6FD]"
                  >
                    <Pencil size={18} className="text-[#6E7397]" />
                    Edit roles
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={(event) => {
                      event.stopPropagation();
                      onAddPermissions(m);
                    }}
                    className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#2C2F44] outline-none transition-colors focus:bg-[#F5F6FD]"
                  >
                    <KeySquare size={18} className="text-[#6E7397]" />
                    Add permissions
                  </DropdownMenu.Item>
                </>
              )}
              <DropdownMenu.Item
                onSelect={(event) => {
                  event.stopPropagation();
                  onRemoveMember(m);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-[14px] px-3 py-2 text-[16px] font-[450] text-[#E24B4B] outline-none transition-colors focus:bg-[#FFF1F1]"
              >
                <UserRemove size={18} className="text-[#E24B4B]" />
                Remove member
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-full hover:bg-[#F3F4FF]"
          aria-label={`Open ${m.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onOpenMember(m);
          }}
        >
          <ChevronRight className="text-[#2C2F44]" size={18} />
        </button>
      </div>
    ),
  },
];
